import React from 'react';
import { connect } from 'react-redux';
import {
  Card,
  CardContent,
  Tooltip,
  IconButton
} from '@mui/material';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { updateValuationInfo } from '../../../store/modules/generalData/actions';

function ValuationInfo({ valuationInfo, dispatchUpdateValuationInfo }) {
  const handleChange = e => {
    const { name, value } = e.target;
    dispatchUpdateValuationInfo(name, value);
  };

  return (
    <>
      {valuationInfo ?
        (<div className="p-4 space-y-4">
          <div className="flex flex-wrap -mx-2 items-stretch">
            
            {/* left column: two stacked cards */}
            <div className="w-full md:w-1/2 px-2 mb-4 flex flex-col space-y-10">
              {/* top-left card stays its natural height */}
              <Card className="flex-none">
                <CardContent className="flex-1 space-y-4">
                  {/** helper to render a label+input with tooltip **/}
                  <h3 className="font-semibold mb-2">Date Information</h3>
                  {[
                    {
                      name: 'effectiveDate',
                      label: 'Effective Date',
                      tooltip: 'The date as of which the value opinion applies',
                      type: 'date'
                    },
                    {
                      name: 'reportDate',
                      label: 'Report Date',
                      tooltip: 'The date the valuation report is completed and signed',
                      type: 'date'
                    },
                    {
                      name: 'inspectionDate',
                      label: 'Inspection Date',
                      tooltip: 'The date the valuation analyst personally inspected the subject property or business',
                      type: 'date'
                    },
                    {
                      name: 'yearToDate',
                      label: 'Year to Date',
                      tooltip: 'Refers to financial data from the beginning of the current year to a specified date',
                      type: 'date'
                    }
                  ].map(field => (
                    <div key={field.name}>
                      <label className="block">
                        <div className="flex items-center mb-1">
                          <span className="block text-sm font-medium mb-1">{field.label}</span>
                          <Tooltip title={field.tooltip} placement="right">
                            <IconButton size="small" className="ml-1 p-0">
                              <InfoOutlinedIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </div>
                        <input
                          type={field.type}
                          name={field.name}
                          value={valuationInfo[field.name] || ''}
                          onChange={handleChange}
                          className="w-full border rounded px-3 py-2"
                        />
                      </label>
                    </div>
                  ))}
                </CardContent>
              </Card>

              {/* bottom-left card stretches to fill half of the column */}
              <Card className="flex-1">
                <CardContent className="flex-1 space-y-4">
                  {/** helper to render a label+input with tooltip **/}
                  <h3 className="font-semibold mb-2">Analyst Information</h3>
                  
                  <div>
                    <label className="block">
                      <span className="block text-sm font-medium mb-1">Analyst Name</span>
                      <input
                        name="analystName"
                        value={valuationInfo?.analystName || ''}
                        onChange={handleChange}
                        rows={1}
                        className="w-full border rounded px-3 py-2"
                      />
                    </label>
                  </div>
                  <div>
                    <label className="block">
                      <span className="block text-sm font-medium mb-1">Preparer Credentials</span>
                      <textarea
                        name="credentials"
                        value={valuationInfo?.credentials || ''}
                        onChange={handleChange}
                        rows={2}
                        className="w-full border rounded px-3 py-2"
                      />
                    </label>
                  </div>
                  <div>
                    <label className="block">
                      <span className="block text-sm font-medium mb-1">Title</span>
                      <input
                        name="title"
                        value={valuationInfo?.title || ''}
                        onChange={handleChange}
                        rows={1}
                        className="w-full border rounded px-3 py-2"
                      />
                    </label>
                  </div>
                  {/* <div>
                    <label className="block">
                      <span className="block text-sm font-medium mb-1">Business Prepare</span>
                      <textarea
                        name="businessPrepare"
                        value={valuationInfo?.businessPrepare || ''}
                        onChange={handleChange}
                        rows={2}
                        className="w-full border rounded px-3 py-2"
                      />
                    </label>
                  </div> */}
                  <div>
                    <label className="block">
                      <span className="block text-sm font-medium mb-1">Analyst Email</span>
                      <input
                        type="email"
                        name="email"
                        value={valuationInfo?.email || ''}
                        onChange={handleChange}
                        rows={1}
                        className="w-full border rounded px-3 py-2"
                      />
                    </label>
                  </div>
                  <div>
                    <label className="block">
                      <span className="block text-sm font-medium mb-1">Analyst Phone</span>
                      <input
                        name="phone"
                        value={valuationInfo?.phone || ''}
                        onChange={handleChange}
                        rows={1}
                        className="w-full border rounded px-3 py-2"
                      />
                    </label>
                  </div>
                  <div>
                    <label className="block">
                      <span className="block text-sm font-medium mb-1">Resume Note</span>
                      <textarea
                        name="resumeNote"
                        value={valuationInfo?.resumeNote || ''}
                        onChange={handleChange}
                        rows={3}
                        className="w-full border rounded px-3 py-2"
                      />
                    </label>
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* right column: single card that stretches to the same height as left column */}
            <div className="w-full md:w-1/2 px-2 mb-4 flex">
              <Card className="flex flex-col flex-1">
                <CardContent className="flex-1 space-y-4">
                  <h3 className="font-semibold mb-2">Report Type</h3>

                  {/** Open‐note fields **/}
                  <div>
                    <label className="block">
                      <span className="block text-sm font-medium mb-1">Purpose of Valuation</span>
                      <textarea
                        name="purposeOfValuation"
                        value={valuationInfo?.purposeOfValuation || ''}
                        onChange={handleChange}
                        rows={3}
                        className="w-full border rounded px-3 py-2"
                      />
                    </label>
                  </div>
                  <div>
                    <label className="block">
                      <span className="block text-sm font-medium mb-1">Intended User of the Report</span>
                      <textarea
                        name="intendedUser"
                        value={valuationInfo?.intendedUser || ''}
                        onChange={handleChange}
                        rows={1}
                        className="w-full border rounded px-3 py-2"
                      />
                    </label>
                  </div>
                  <div>
                    <label className="block">
                      <span className="block text-sm font-medium mb-1">
                        Subject Interest Amount Valued (%)
                      </span>
                      <input
                        type="number"
                        name="subjectInterest"
                        value={valuationInfo?.subjectInterest || ''}
                        onChange={handleChange}
                        className="w-full border rounded px-3 py-2"
                      />
                    </label>
                  </div>

                  {/** Dropdowns **/}
                  {[
                    {
                      name: 'formOfReport',
                      label: 'Form of Report',
                      options: [
                        { value: '', label: 'Select…' },
                        { value: 'Calculation Engagement', label: 'Calculation Engagement' },
                        { value: 'Valuation Engagement', label: 'Valuation Engagement' }
                      ]
                    },
                    {
                      name: 'typeOfReport',
                      label: 'Type of Report',
                      options: [
                        { value: '', label: 'Select…' },
                        { value: 'Detailed', label: 'Detailed' },
                        { value: 'Summary', label: 'Summary' },
                        { value: 'Calculation', label: 'Calculation' }
                      ]
                    },
                    {
                      name: 'standardOfValue',
                      label: 'Standard of Value',
                      options: [
                        { value: '', label: 'Select…' },
                        { value: 'Fair Market Value', label: 'Fair Market Value' },
                        { value: 'Investment Value', label: 'Investment Value' },
                        { value: 'Fair Value', label: 'Fair Value' }
                      ]
                    },
                    {
                      name: 'premiseOfValue',
                      label: 'Premise of Value',
                      options: [
                        { value: '', label: 'Select…' },
                        { value: 'Going Concern', label: 'Going Concern' },
                        { value: 'Liquidation Value', label: 'Liquidation Value' }
                      ]
                    },
                    {
                      name: 'controllingInterest',
                      label: 'Controlling Interest',
                      options: [
                        { value: '', label: 'Select…' },
                        { value: 'Controlling', label: 'Controlling' },
                        { value: 'Non-Controlling', label: 'Non-Controlling' }
                      ]
                    },
                    {
                      name: 'marketability',
                      label: 'Marketability',
                      options: [
                        { value: '', label: 'Select…' },
                        { value: 'Marketable', label: 'Marketable' },
                        { value: 'Non-Marketable', label: 'Non-Marketable' }
                      ]
                    },
                    {
                      name: 'discountForLackOfControl',
                      label: 'Discount for Lack of Control',
                      options: [
                        { value: '', label: 'Select…' },
                        { value: 'Does Apply', label: 'Does Apply' },
                        { value: 'Does Not Apply', label: 'Does Not Apply' }
                      ]
                    },
                    {
                      name: 'discountForLackOfMarketability',
                      label: 'Discount for Lack of Marketability',
                      options: [
                        { value: '', label: 'Select…' },
                        { value: 'Does Apply', label: 'Does Apply' },
                        { value: 'Does Not Apply', label: 'Does Not Apply' }
                      ]
                    }
                  ].map(field => (
                    <div key={field.name}>
                      <label className="block">
                        <span className="block text-sm font-medium mb-1">{field.label}</span>
                        <select
                          name={field.name}
                          value={valuationInfo[field.name] || ''}
                          onChange={handleChange}
                          className="w-full text-sm border rounded px-3 py-2"
                        >
                          {field.options.map(o => (
                            <option key={o.value} value={o.value}>{o.label}</option>
                          ))}
                        </select>
                      </label>
                    </div>
                  ))}

                </CardContent>
              </Card>
            </div>
          </div>
        </div>)
        :
        (<div/>)
      }
    </>
  );
}

const mapStateToProps = state => ({
  valuationInfo: state.generalData.valuationInfo
});

const mapDispatchToProps = dispatch => ({
  dispatchUpdateValuationInfo(name, value) {
    dispatch(updateValuationInfo(name, value));
  }
});

export default connect(mapStateToProps, mapDispatchToProps)(ValuationInfo);